import { KeyboardEvent, ReactElement, ReactNode, cloneElement } from "react";
import { Card, Flex, Typography } from "antd";
import { RightOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import NavSectionLabel from "components/NavSectionLabel.component";
import { IconTone, ICON_TONES } from "shared/theme/color";
import { colors } from "shared/theme/color";
import { textPx } from "shared/constants/zoom";

// The grouped list both hub pages are built from: one bordered card per
// section, holding a vertical stack of rows. A list scans top-to-bottom, rows
// are about half a tile's height, and a row's value gets an aligned column of
// its own instead of fighting the description for width.
//
// The icon chip takes a tone per GROUP, never per row: a hue that varied
// inside a group would cut across the structure its heading just set up.
// Pengaturan tints two of its five groups and leaves three neutral; Laporan
// passes neutral throughout (its rows are homogeneous, so a hue would restate
// the heading). Both are callers' decisions, not this component's.
//
// Pengaturan adopted this first and Laporan followed, which is why it sits in
// components/ rather than under either page. Pengaturan rows carry a value
// (a setting's current state); Laporan rows are pure navigation and pass
// reserveMeta={false} so they don't hold an empty column open.

export const HubGroup = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <div>
    <NavSectionLabel>{label}</NavSectionLabel>
    <Card
      className="hub-group"
      style={{ borderRadius: 16, borderColor: colors.line, marginTop: 8 }}
      styles={{ body: { padding: 0 } }}
    >
      {children}
    </Card>
  </div>
);

interface HubRowProps {
  /** A TwoTone icon element; the row tints it to match its group. */
  icon: ReactElement;
  title: string;
  description: string;
  tone?: IconTone;
  /** The row's current value, right-aligned. Reserves width while undefined. */
  meta?: ReactNode;
  /**
   * Whether to hold the value column open. True for rows whose value arrives
   * with a query (Pengaturan): the reservation stops the description
   * re-wrapping when it lands. False for rows that never have one (Laporan),
   * which would otherwise carry 96px of empty space before the chevron.
   * Cannot be inferred from `meta === undefined`, because that is exactly the
   * loading state the reservation exists for.
   */
  reserveMeta?: boolean;
  /**
   * Paints the value in the quieter grey. Used for "Off", so scanning the
   * column reads as "what is switched on" without a dot or badge.
   */
  muted?: boolean;
  /** Route to navigate to. Renders a real link. Mutually exclusive with onClick. */
  to?: string;
  /** For rows that open a flow in place (a modal) rather than navigating. */
  onClick?: () => void;
  /**
   * An in-place control (e.g. the language toggle) rendered where the value
   * and chevron would be. A row with a control is static: it neither links
   * nor acts, the control does.
   */
  control?: ReactNode;
}

export const HubRow = ({
  icon,
  title,
  description,
  tone = "neutral",
  meta,
  reserveMeta = true,
  muted = false,
  to,
  onClick,
  control,
}: HubRowProps) => {
  const chip = ICON_TONES[tone];
  const interactive = !control && (to !== undefined || onClick !== undefined);

  // TwoTone icons take [primary, secondary]: the outline in the group's hue,
  // the fill in white so the shape lifts off the tinted chip.
  const tintedIcon = cloneElement(icon, {
    twoToneColor: [chip.fg, colors.white],
  });

  const body = (
    <Flex align="center" gap={14} style={{ padding: "14px 20px", minHeight: 60 }}>
      <div
        style={{
          width: 40,
          height: 40,
          flexShrink: 0,
          borderRadius: 12,
          background: chip.bg,
          // Outlined icons inherit this; TwoTone icons carry explicit fills
          // from twoToneColor below and are unaffected.
          color: chip.fg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: textPx(20),
        }}
      >
        {tintedIcon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Typography.Text
          strong
          style={{ fontSize: textPx(14), lineHeight: `${textPx(20)}px`, display: "block" }}
        >
          {title}
        </Typography.Text>
        <Typography.Text
          style={{
            fontSize: textPx(13),
            lineHeight: `${textPx(19)}px`,
            color: colors.ink2,
            display: "block",
            marginTop: 2,
          }}
        >
          {description}
        </Typography.Text>
        {/* Phone only (CSS): the value sits under the description instead of
            beside it, so a long value like "4 Metode Pembayaran" doesn't
            squeeze the text into a sliver. */}
        {meta !== undefined && !control && (
          <Typography.Text
            className="tnum hub-row-meta-inline"
            style={{
              fontSize: textPx(12.5),
              color: muted ? colors.ink3 : colors.ink2,
              marginTop: 4,
            }}
          >
            {meta}
          </Typography.Text>
        )}
      </div>
      {control ?? (
        <>
          {reserveMeta && (
          <Typography.Text
            className="tnum hub-row-meta"
            style={{
              flexShrink: 0,
              textAlign: "right",
              fontSize: textPx(13),
              color: muted ? colors.ink3 : colors.ink2,
            }}
          >
            {meta}
          </Typography.Text>
          )}
          {interactive && (
            <RightOutlined
              style={{ color: colors.ink3, fontSize: textPx(12), flexShrink: 0 }}
            />
          )}
        </>
      )}
    </Flex>
  );

  if (control || !interactive) {
    return <div className="hub-row hub-row--static">{body}</div>;
  }

  // A row that IS a destination is a link: Enter, middle-click, ctrl-click
  // and the screen-reader link list all come for free. One that opens a flow
  // in place is a button instead.
  if (to) {
    return (
      <Link to={to} className="hub-row hub-row--clickable">
        {body}
      </Link>
    );
  }

  const activate = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Enter" && e.key !== " ") return;
    e.preventDefault();
    onClick?.();
  };

  return (
    <div
      role="button"
      tabIndex={0}
      className="hub-row hub-row--clickable"
      onClick={onClick}
      onKeyDown={activate}
    >
      {body}
    </div>
  );
};
